import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { TargetData } from "../types";
import {
  handleFileChange,
  handleDelete,
  handleEdit,
  handleImport,
  fetchTargets,
  handleDeleteNight,
  processTargetData,
  normalizeTargetName,
  deleteFramesByType,
  deleteTargetById,
} from "../utils/dataProcessing";
import { useDialog } from "./DialogContext";
import { useAuth } from "./AuthContext";

interface TargetContextType {
  targets: TargetData[];
  loading: boolean;
  error: string | null;
  selectedTarget: TargetData | null;
  selectedNight: string | null;
  targetLastSelectedNight: Record<string, string>;
  acquisitionFile: File | null;
  metadataFile: File | null;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setSelectedTarget: (target: TargetData | null) => void;
  setSelectedNight: (night: string | null) => void;
  setTargetLastSelectedNight: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "acquisition" | "metadata"
  ) => void;
  handleDelete: () => Promise<void>;
  handleEdit: (updates: Partial<TargetData>) => Promise<void>;
  handleImport: () => Promise<void>;
  handleDeleteNight: (night: string) => Promise<void>;
  setAcquisitionFile: (file: File | null) => void;
  setMetadataFile: (file: File | null) => void;
  handleDeleteFramesByType: (
    frameType: "LIGHT" | "DARK" | "FLAT" | "BIAS",
    night?: string,
    targetOverride?: TargetData | null
  ) => Promise<void>;
  deleteFramesByType: (
    target: TargetData,
    frameType: "LIGHT" | "DARK" | "FLAT" | "BIAS",
    night?: string
  ) => TargetData;
  fetchTargets: () => Promise<TargetData[]>;
  deleteTargetById: (id: string | string[]) => Promise<void>;
}

const TargetContext = createContext<TargetContextType | undefined>(undefined);

export const TargetProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [targets, setTargets] = useState<TargetData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<TargetData | null>(null);
  const [selectedNight, setSelectedNight] = useState<string | null>(null);
  const [targetLastSelectedNight, setTargetLastSelectedNight] = useState<
    Record<string, string>
  >({});
  const [acquisitionFile, setAcquisitionFile] = useState<File | null>(null);
  const [metadataFile, setMetadataFile] = useState<File | null>(null);
  const [search, setSearch] = useState<string>("");

  const { setEditDialogOpen } = useDialog();
  const { isAuthenticated, token } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchTargetsBound = useCallback(
    fetchTargets({ setTargets, setError, setLoading }),
    []
  );

  useEffect(() => {
    console.log(
      "TargetContext mounted, authentication status:",
      isAuthenticated
    );
    // Only fetch targets if the user is authenticated
    if (isAuthenticated && token) {
      console.log("User is authenticated, fetching targets");
      setLoading(true); // Set loading to true before fetching
      fetchTargetsBound();
    } else {
      console.log("User is not authenticated, skipping target fetch");
      // Clear any existing targets when not authenticated
      setTargets([]);
      setLoading(false); // Ensure loading is false when not authenticated
    }
  }, [isAuthenticated, token, fetchTargetsBound]);

  const handleFileChangeBound = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      type: "acquisition" | "metadata"
    ) => {
      handleFileChange({ setAcquisitionFile, setMetadataFile })(event, type);
    },
    [setAcquisitionFile, setMetadataFile]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeleteBound = useCallback(
    handleDelete({
      selectedTarget,
      setSelectedTarget,
      setSelectedNight,
      setError,
      fetchTargets: fetchTargetsBound,
    }),
    [
      selectedTarget,
      setSelectedTarget,
      setSelectedNight,
      setError,
      fetchTargetsBound,
    ]
  );

  const handleEditBound = useCallback(
    async (updates: Partial<TargetData>) => {
      if (selectedTarget) {
        const updatedTarget = { ...selectedTarget, ...updates };
        await handleEdit({
          selectedTarget,
          setSelectedTarget,
          setEditDialogOpen,
          setError,
          fetchTargets: fetchTargetsBound,
          selectedNight,
        })(updatedTarget);
      }
    },
    [
      selectedTarget,
      setSelectedTarget,
      setEditDialogOpen,
      setError,
      fetchTargetsBound,
      selectedNight,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleImportBound = useCallback(
    handleImport({
      acquisitionFile,
      metadataFile,
      setError,
      setLoading,
      fetchTargets: fetchTargetsBound,
      setSelectedTarget,
      setAcquisitionFile,
      setMetadataFile,
      processTargetData,
      normalizeTargetName,
    }),
    [
      acquisitionFile,
      metadataFile,
      setError,
      setLoading,
      fetchTargetsBound,
      setSelectedTarget,
      setAcquisitionFile,
      setMetadataFile,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeleteNightBound = useCallback(
    handleDeleteNight({
      selectedTarget,
      setSelectedTarget,
      setSelectedNight,
      setError,
      fetchTargets: fetchTargetsBound,
    }),
    [
      selectedTarget,
      setSelectedTarget,
      setSelectedNight,
      setError,
      fetchTargetsBound,
    ]
  );

  const handleDeleteFramesByType = useCallback(
    async (
      frameType: "LIGHT" | "DARK" | "FLAT" | "BIAS",
      night?: string,
      targetOverride?: TargetData | null
    ) => {
      const target = targetOverride || selectedTarget;
      if (!target) return;
      // Only proceed if there are frames of this type (and night, if specified)
      const framesToDelete = target.images.filter((img) => {
        if (img.FrameType !== frameType) return false;
        if (night) {
          const match = img.FilePath.match(/DATE_(\d{4}-\d{2}-\d{2})/);
          return match ? match[1] === night : false;
        }
        return true;
      });
      if (framesToDelete.length === 0) return; // nothing to delete
      const updatedTarget = deleteFramesByType(target, frameType, night);
      await handleEdit({
        selectedTarget: target,
        setSelectedTarget,
        setEditDialogOpen,
        setError,
        fetchTargets: fetchTargetsBound,
      })(updatedTarget);
      // Always refresh targets after deletion
      await fetchTargetsBound();
    },
    [
      selectedTarget,
      setSelectedTarget,
      setEditDialogOpen,
      setError,
      fetchTargetsBound,
    ]
  );

  const deleteTargetByIdBound = useCallback(
    async (id: string | string[]) => {
      await deleteTargetById(id, fetchTargetsBound, setError);
    },
    [fetchTargetsBound, setError]
  );

  return (
    <TargetContext.Provider
      value={{
        targets,
        loading,
        error,
        selectedTarget,
        selectedNight,
        targetLastSelectedNight,
        acquisitionFile,
        metadataFile,
        search,
        setSearch,
        setSelectedTarget,
        setSelectedNight,
        setTargetLastSelectedNight,
        handleFileChange: handleFileChangeBound,
        handleDelete: handleDeleteBound,
        handleEdit: handleEditBound,
        handleImport: handleImportBound,
        handleDeleteNight: handleDeleteNightBound,
        setAcquisitionFile,
        setMetadataFile,
        handleDeleteFramesByType,
        deleteFramesByType,
        fetchTargets: fetchTargetsBound,
        deleteTargetById: deleteTargetByIdBound,
      }}
    >
      {children}
    </TargetContext.Provider>
  );
};

export const useTarget = () => {
  const context = useContext(TargetContext);
  if (context === undefined) {
    throw new Error("useTarget must be used within a TargetProvider");
  }
  return context;
};
